import React from "react";
import styled from "styled-components";
import { Card, Empty, Progress, Space, Tag, Typography } from "antd";

const { Text } = Typography;

const WidgetCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  margin-bottom: 14px;
`;

const SummaryTile = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
`;

const VehicleList = styled.div`
  display: grid;
  gap: 12px;
`;

const VehicleRow = styled.div`
  border: 1px solid ${({ $state }) => ($state === "unavailable" ? "#ffccc7" : "#f0f0f0")};
  border-radius: 8px;
  padding: 12px;
  background: ${({ $state }) => ($state === "unavailable" ? "#fff2f0" : "#ffffff")};
`;

const VehicleHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const VehicleUtilizationWidget = ({ vehicleUtilization }) => {
  if (!vehicleUtilization?.vehicles?.length) {
    return (
      <WidgetCard title="Vehicle Utilization">
        <Empty
          description="No vehicle utilization data for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </WidgetCard>
    );
  }

  const summary = vehicleUtilization.summary || {};

  return (
    <WidgetCard
      title="Vehicle Utilization"
      extra={<Tag color="blue">{formatNumber(summary.totalVehicles)} vehicles</Tag>}
      data-testid="vehicle-utilization-widget"
    >
      <SummaryGrid>
        <SummaryValue label="Active" value={summary.activeVehicles} color="green" />
        <SummaryValue label="Idle" value={summary.idleVehicles} color="default" />
        <SummaryValue label="Unavailable" value={summary.unavailableVehicles} color="red" />
        <SummaryValue
          label="Avg Capacity"
          value={
            summary.averageCapacityUsed == null
              ? "Missing"
              : `${summary.averageCapacityUsed}%`
          }
          color="gold"
        />
      </SummaryGrid>

      <VehicleList>
        {vehicleUtilization.vehicles.map((vehicle) => (
          <VehicleRow
            key={vehicle.drilldownKey}
            $state={vehicle.utilizationState}
            data-drilldown-key={vehicle.drilldownKey}
          >
            <VehicleHeader>
              <Space size={6} wrap>
                <Text strong>{vehicle.label}</Text>
                <Tag color={stateColor(vehicle.utilizationState)}>
                  {vehicle.utilizationState}
                </Tag>
              </Space>
              <Text>{formatNumber(vehicle.activeTripCount)} active trips</Text>
            </VehicleHeader>
            <Progress
              percent={vehicle.averageCapacityUsed ?? 0}
              showInfo={vehicle.averageCapacityUsed != null}
              strokeColor={vehicle.averageCapacityUsed >= 90 ? "#faad14" : "#005249"}
            />
            <Space size={[6, 6]} wrap>
              <Tag>{vehicle.status}</Tag>
              {vehicle.vehicleType && <Tag>{vehicle.vehicleType}</Tag>}
              {vehicle.topRoute && <Tag>Route: {vehicle.topRoute}</Tag>}
              {vehicle.capacityKg != null && <Tag>{formatNumber(vehicle.capacityKg)} kg</Tag>}
            </Space>
          </VehicleRow>
        ))}
      </VehicleList>

      {vehicleUtilization.assumptions?.length > 0 && (
        <Text type="secondary">
          Assumption: {vehicleUtilization.assumptions[0]}
        </Text>
      )}
    </WidgetCard>
  );
};

const SummaryValue = ({ label, value, color }) => (
  <SummaryTile>
    <Space direction="vertical" size={2}>
      <Text type="secondary">{label}</Text>
      <Tag color={color}>{value ?? 0}</Tag>
    </Space>
  </SummaryTile>
);

function stateColor(state) {
  if (state === "active") return "green";
  if (state === "unavailable") return "red";
  return "default";
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-ZA").format(Number(value || 0));
}

export default VehicleUtilizationWidget;
